<template>
    <div class="p-d-basic" :class="{'has-value': valueInput}">
        <div class="main-box">
            <template v-if="valueInput">
                <textarea class="value-box" v-if="useTextarea" readonly
                          v-model="renderedValue" ref="textarea"></textarea>
                <div v-else class="value-box">{{renderedValue}}</div>
            </template>
            <div v-else class="empty-box">Empty</div>
        </div>
        <div class="locate-button mt-1" v-if="canLocate" @click="tryToLocate">
            <i class="gf gf-pin"></i>
        </div>
    </div>
</template>

<script>
import {
    buildORS,
    deepCompare, evaluateFieldValueFilled,
    evaluateValueFilled,
    numberToKMB,
    numberWithCommas, renderValueInCandidateItem
} from "@/utils";
    import {currencyIdToLabel, currencyMaps} from "@/config";
    import numWords from 'num-words';
    import moment from 'moment';

    export default {
        name: "PDBasic",
        props: ['value', 'field', 'candidates',],
        computed: {
            renderedValue() {
                let {value, field} = this;

                if (!this.valueInput) {
                    return '';
                }

                return renderValueInCandidateItem(value, field);
            },
            valueInput() {
                let {value, field} = this;

                return evaluateFieldValueFilled(value, field)
            },
            canLocate() {
                if (this.candidateContextToLocate) {
                    return true;
                }
                return this.valueInput && this.innocentValueWorthLocating;
            },
            candidateContextToLocate() {
                const {candidates, value} = this;
                if (candidates) {
                    for (let i = 0; i < candidates.length; i++) {
                        let candidate = candidates[i];
                        if (deepCompare(candidate.value, value)) {
                            let cCtx = candidate.context;
                            if (cCtx && (cCtx = cCtx.trim())) {
                                const item = {
                                    context: cCtx,
                                };
                                if (evaluateValueFilled(candidate.seed)) {
                                    item.seed = candidate.seed;
                                }
                                return item;
                            }
                        }
                    }
                }
                return null;
            },
            innocentValueWorthLocating() {
                return this.field.field_name !== 'Transaction status' &&
                    this.value !== 'Not Mentioned' && typeof this.value !== 'boolean' && this.field.data_type !== 'multiple_list' && this.field.data_type !== 'multiple_list_v2';
            },
            innocentValueToLocate() {
                const {renderedValue, value, field} = this;

                const dataType = field.data_type;
                if (dataType === 'ratio') {
                    let dataFormats, rightProperty, rightValue;
                    dataFormats = field['data_formats'];
                    rightProperty = dataFormats[1]['entry_name'];
                    rightValue = value[rightProperty];
                    return `for every ${buildORS(rightValue, numWords(rightValue))}`;
                }

                if (dataType === 'date') {
                    let date = moment(value, 'X');
                    return `${buildORS(date.format('D MMM YYYY'),
                        date.format('D MMMM YYYY'),
                        date.format('D MMMM'))}`;
                }

                if (field.data_type === 'range') {
                    let fromValue;
                    let toValue;
                    let unit;
                    let entryPrefixContainer = [];
                    let entrySuffix = '';
                    let hasFromValue;
                    let hasToValue;
                    let symbol;
                    let ors = [];
                    let pureValues = [];

                    if ('unit' in field) {
                        fromValue = value.amount.from;
                        toValue = value.amount.to;
                        unit = value[field.unit];
                        if (field.unit === 'currency') {
                            let currencyLabel = currencyIdToLabel[unit];
                            if (currencyLabel in currencyMaps) {
                                symbol = currencyMaps[currencyLabel].symbol;
                                if (Array.isArray(symbol)) {
                                    entryPrefixContainer = entryPrefixContainer.concat(symbol);
                                } else {
                                    entryPrefixContainer.push(symbol);
                                }
                            }
                        } else if (field.unit === 'percentage') {
                            entrySuffix = '%';
                        }
                    } else {
                        fromValue = value.from;
                        toValue = value.to;
                    }

                    hasFromValue = evaluateValueFilled(fromValue);
                    hasToValue = evaluateValueFilled(toValue);

                    if (field.range_entry_type === 'date') {
                        if (hasFromValue) {
                            let date = moment(fromValue, 'X');
                            pureValues.push(date.format('D MMMM YYYY'))
                        }
                        if (hasToValue) {
                            let date = moment(toValue, 'X');
                            pureValues.push(date.format('D MMMM YYYY'));
                        }
                    } else if (field.range_entry_type === 'decimal') {
                        if (hasFromValue) {
                            pureValues.push(fromValue);
                        }
                        if (hasToValue) {
                            pureValues.push(toValue);
                        }
                    }

                    pureValues.forEach((v) => {
                        let forms1 = [];
                        let forms2 = [];
                        let temp;
                        forms1.push(v.toString());
                        if (v.constructor === Number) {
                            temp = numberWithCommas(v);
                            if (temp !== v.toString()) {
                                forms1.push(temp);
                            }
                            temp = numberToKMB(v);
                            if (temp !== v.toString()) {
                                forms1.push(temp);
                            }
                        }
                        if (entryPrefixContainer.length || entrySuffix) {
                            if (!entryPrefixContainer.length) {
                                entryPrefixContainer.push('');
                            }
                            entryPrefixContainer.forEach((entryPrefix) => {
                                forms1.forEach((vv) => {
                                    forms2.push(entryPrefix + vv + entrySuffix);
                                })
                            })
                        }

                        ors = ors.concat(forms2, forms1);
                    });

                    return buildORS(...ors);
                }

                if ('unit' in field) {
                    const unit = value[field.unit];
                    let amount = value['amount'];
                    let currencySeeds;
                    let symbol;

                    if (field.unit === 'currency') {
                        let currencyLabel = currencyIdToLabel[unit];
                        if (currencyLabel in currencyMaps) {
                            currencySeeds = [currencyLabel,]
                            symbol = currencyMaps[currencyLabel].symbol;
                            if (Array.isArray(symbol)) {
                                currencySeeds = currencySeeds.concat(symbol);
                            } else {
                                currencySeeds.push(symbol);
                            }
                            currencyLabel = `${buildORS(...currencySeeds)}`;
                        }
                        return `${currencyLabel} ${buildORS(amount, numberToKMB(amount), numberWithCommas(amount))}`;
                    }

                    if (field.unit === 'percentage') {
                        let amountString = String(amount);
                        let ors = [amountString + '%'];
                        let amountFixed2String = amount.toFixed(2);
                        if (amountFixed2String !== amountString) {
                            ors.push(amountFixed2String + '%');
                        }
                        if (amount < 0) {
                            amount = Math.abs(amount);
                            amountString = String(amount);
                            ors.push(amountString + '%');
                            amountFixed2String = amount.toFixed(2);
                            if (amountFixed2String !== amountString) {
                                ors.push(amountFixed2String + '%');
                            }
                        }
                        return `${buildORS(...ors)}`;
                    } else {
                        return `${amount} ${unit}`;
                    }
                }

                if (value.constructor === Number) {
                    let ors = [numberToKMB(value), numberWithCommas(value),];
                    if (value < 0) {
                        ors.push(numberWithCommas(Math.abs(value)));
                    }
                    return buildORS(...ors)
                }

                return renderedValue;
            },
            locateContext() {
                const {candidateContextToLocate} = this;
                if (candidateContextToLocate) {
                    let {context, seed} = candidateContextToLocate;
                    if (!seed && this.innocentValueWorthLocating) {
                        seed = this.innocentValueToLocate;
                    }
                    if (seed) {
                        context += `:::${seed}`;
                    }
                    return context;
                }

                return this.innocentValueToLocate;
            },
            useTextarea() {
                return this.field.data_type === 'text' && this.value !== 'Not Mentioned';
            }
        },
        methods: {
            tryToLocate() {
                this.$emit('highlightTextInViewerForItem', {context: this.locateContext});
            },
        },
    }
</script>

<style scoped lang="scss">
    @import "../../assets/variables";

    .main-box {
        border: 1px solid #c0c0c0;
        border-radius: 4px;
        background-color: white;
        min-height: 23px;
        display: flex;
    }

    .has-value {
        .main-box {
        }
    }

    .value-box {
        line-height: 1.1rem;
        padding: 2px 1rem 2px 0.32rem;
        width: 100%;
        border: none;
        box-shadow: none;
    }

    textarea.value-box {
        resize: vertical;
        min-height: 3.3rem;
    }

    .locate-button {
        cursor: pointer;
        background-color: rgba(black, 0.06);
        color: #666666;
        font-size: 0.86em;
        padding: 3px 6px;
        border-radius: 4px;
        width: 100%;
        text-align: center;

        &:hover {
            color: $primary-blue;
            background-color: rgba($primary-blue, 0.1);
        }

        &:active {
            box-shadow: inset 0 0 0 1px rgba($primary-blue, 0.6);
        }
    }

    .empty-box {
        font-style: italic;
        color: gray;
        padding: 2px 0.32rem;
    }
</style>
